import { Input, Label, TextEditor } from '@/components/basic';
import { RESOURCES_TEXT } from '@/components/partial/Cards/Resources/ListResources';
import { EditResourceForm } from '@/components/partial/ResourceSettingsPanel/ResourceSettingsPanel';
import { ResourceMakeUnavailableControl } from '@/components/reserve/controls/ResourceMakeUnavailableControl';
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from '@/hooks/useCustomParkingVerbiage';
import { classNames } from '@/lib/classNames';
import { isLabelFieldEnabled } from '@/lib/featureFlags';
import { IResource } from '@gettactic/api';
import { capitalize } from '@gettactic/helpers';
import { useAreas, useGetAmenities } from '@gettactic/hooks';
import { useAuthenticated } from '@gettactic/hooks';
import pluralize from 'pluralize';
import React, { useEffect, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import Select from 'react-select';
import { TagsInput } from '@v2/components/form/TagsInput/TagsInput';
import { useIsDesignSystem2 } from '@webV2/helpers/useIsDesignSystem2';

export interface GeneralPanelProps {
  entity: 'workspace' | 'meeting_room' | 'parking' | 'area' | 'zone';
  areaList: ReturnType<typeof useAreas>['areas'];
  defaultValues: EditResourceForm;
  refSubmit: React.RefObject<HTMLButtonElement>;
  resource: IResource | null;
  form: UseFormReturn<EditResourceForm>;
}

export const GeneralPanel = ({
  resource,
  refSubmit,
  entity,
  areaList,
  form,
  defaultValues
}: GeneralPanelProps) => {
  const { control, register, setValue, watch } = form;

  const isV2 = useIsDesignSystem2();
  const amenitiesResponse = useGetAmenities();
  const amenities = amenitiesResponse?.data?.result?.tags || [];

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const currentIsAvailable = watch('is_unavailable');
  watch('is_open');
  useEffect(() => {
    const keyBinding = (e: KeyboardEvent) => {
      // checking that we aren't messing things with inputs
      if (
        e.key === 'a' &&
        e.target instanceof Element &&
        e.target.tagName.toLowerCase() !== 'input' &&
        e.target.className.toLowerCase().indexOf('prosemirror') < 0
      ) {
        setValue('is_unavailable', !currentIsAvailable);
        if (refSubmit.current) {
          // because modal gets by default focus on the close button
          // this allows to use the hotkey and then immediately save with ENTER
          refSubmit.current.focus();
        }
      }
    };
    document.addEventListener('keydown', keyBinding);
    return () => document.removeEventListener('keydown', keyBinding);
  }, [currentIsAvailable]);

  const isParking =
    resource?.type === 'parking_space' || resource?.type === 'parking_lot';

  const customParkingVerbiage = useCustomParkingVerbiage();
  const { parkingSpaceLabel, parkingLotLabel } = useMemo(() => {
    const parkingSpaceLabel = customParkingVerbiage ?? 'parking space';
    const parkingLotLabel = customParkingVerbiage
      ? `${customParkingVerbiage} group`
      : 'parking lot';
    return { parkingSpaceLabel, parkingLotLabel };
  }, [customParkingVerbiage]);

  const areasWithoutThisOne = useMemo(
    () =>
      areaList?.options.filter(
        (x: { value: string }) => x.value !== resource?.id
      ) ?? [],
    [areaList?.options, resource?.id]
  );
  const areaType = resource?.parent_id === null ? 'floor' : 'zone';

  const isMultiple = watch('multiplicity') === 'multiple';
  const selectedAmenities = watch('amenities');

  return resource ? (
      <div className="pr-2">
        <input type="hidden" {...register('maximum_slots_per_user')} />
        <div className="my-4 flex flex-col flex-nowrap items-stretch">
          <Label htmlFor={`${entity}-name`}>
            <h3 className="font-bold text-base text-gray-800">Name</h3>
            <Input
              variant={isV2 ? "v2Simple" : undefined}
              type="text"
              id={`${entity}-name`}
              {...register('name', { required: true })}
              defaultValue={defaultValues.name || ''}
            />
          </Label>

          {resource.type !== 'area' ? (
            <>
              <div
                className={classNames(
                  !isLabelFieldEnabled(authenticatedUser) && 'hidden'
                )}
              >
                <Label htmlFor={`${entity}-label`}>
                  <h3 className="mt-2 font-bold text-base text-gray-800">
                    Label
                  </h3>
                  <Input
                    variant={isV2 ? "v2Simple" : undefined}
                    type="text"
                    id={`${entity}-label`}
                    {...register('label', { required: false })}
                    defaultValue={defaultValues.label || ''}
                  />
                </Label>
              </div>
              {isV2 && (
                <div className={'py-2'}>
                  <TagsInput
                    onChangeValue={(tags) => {
                      form.setValue('amenities', tags);
                    }}
                    selectedTags={selectedAmenities}
                    label={'Amenities'}
                    tags={amenities.map((amenity) => ({
                      value: amenity.id,
                      label: amenity.name,
                      keywords: [amenity.name]
                    }))}
                    placeholder={'Search for amenities'}
                  />
                </div>
              )}
              <Label htmlFor={`${entity}-description`}>
                <h3 className="mt-2 font-bold text-base text-gray-800">
                  Description
                </h3>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: false }}
                  defaultValue={defaultValues.description || ''}
                  render={({ field: { onChange, value } }) => (
                    <TextEditor
                      onChange={onChange}
                      value={value ?? ''}
                      id={`${entity}-description`}
                    />
                  )}
                />
              </Label>
            </>
          ) : null}

          {resource.type !== 'meeting_room' && resource.type !== 'area' ? (
            <>
              <h3
                className="mt-4 font-bold text-base text-gray-800"
                id={`description-${entity}-multiplicity`}
              >
                {isParking ? (
                  <>
                    Is this a single {parkingSpaceLabel} or a {parkingLotLabel}{' '}
                    {customParkingVerbiage ? 'group' : 'lot'} (two or more
                    spaces)?
                  </>
                ) : (
                  <>
                    Is this a single workspace (e.g. an office) or a group of
                    desks?
                  </>
                )}
              </h3>
              <div className="grid grid-cols-12 items-center gap-8">
                <Label
                  radio
                  htmlFor={`${entity}-single`}
                  className="col-span-6 flex items-center gap-2"
                >
                  <Input
                    variant={isV2 ? "v2Simple" : undefined}
                    type="radio"
                    {...register('multiplicity')}
                    id={`${entity}-single`}
                    aria-describedby={`description-${entity}-multiplicity`}
                    defaultValue="single"
                    defaultChecked={defaultValues.multiplicity === 'single'}
                  />
                  <span className="cursor-pointer text-base text-gray-800">
                    {isParking
                      ? `Single ${parkingSpaceLabel}`
                      : 'Single workspace'}
                  </span>
                </Label>
                <Label
                  radio
                  htmlFor={`${entity}-multiple`}
                  className="col-span-6 flex items-center gap-2"
                >
                  <Input  
                    type="radio"
                    {...register('multiplicity')}
                    id={`${entity}-multiple`}
                    aria-describedby={`description-${entity}-multiplicity`}
                    defaultValue="multiple"
                    defaultChecked={defaultValues.multiplicity === 'multiple'}
                  />
                  <span className="cursor-pointer text-base text-gray-800">
                    {isParking ? capitalize(parkingLotLabel) : 'Group'}
                  </span>
                </Label>
              </div>

              {isMultiple ? (
                <>
                  <Label
                    htmlFor={`${entity}-total_slots`}
                    className="mt-4 font-bold text-base text-gray-800"
                  >
                    {isParking && (
                      <>
                        How many {parkingSpaceLabel} are available on this{' '}
                        {parkingLotLabel}?
                      </>
                    )}
                    {!isParking &&
                      'How many total workspaces do you want to make available for reservations?'}
                  </Label>
                  <Input
                    type="text"
                    id={`${entity}-total_slots`}
                    variant={isV2 ? "v2Simple" : undefined}
                    {...register('total_slots', {
                      required: true,
                      valueAsNumber: true,
                      validate: (value) =>
                        value.toString() === '' ||
                        Number.parseInt(value.toString(), 10) > 0
                    })}
                    defaultValue={defaultValues.total_slots || 1}
                  />
                </>
              ) : null}
            </>
          ) : null}

          {areaList && !(resource.type === 'area' && areaType === 'floor') ? (
            <div id={`label-${entity}-parent_id`}>
              <h3 className="mt-4 font-bold text-base text-gray-800">
                Parent Zone or Floor
              </h3>
              {resource.type !== 'meeting_room' && resource.type !== 'area' ? (
                <p className="mt-1 text-base text-gray-800">
                  Organize your{' '}
                  {isParking && customParkingVerbiage
                    ? pluralize(customParkingVerbiage, 2)
                    : RESOURCES_TEXT[entity].plural}{' '}
                  by assigning them to an office zone or floor.
                </p>
              ) : (
                <p className="mb-2 text-base text-gray-800">
                  A zone must belong to a floor or another zone
                </p>
              )}

              <Controller
                control={control}
                name="parent_id"
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    className="w-full"
                    classNamePrefix="react-select"
                    placeholder="Choose an office zone or floor..."
                    aria-labelledby={`label-${entity}-parent_id`}
                    theme={isV2 ? undefined : (theme) => ({
                      ...theme,
                      borderRadius: 6,
                      colors: {
                        ...theme.colors,
                        primary: '#F87E3F',
                        primary25: '#FFE8CE',
                        primary50: '#FFE8CE'
                      }
                    })}
                    defaultValue={defaultValues.parent_id}
                    options={
                      resource?.type === 'area'
                        ? areasWithoutThisOne
                        : areaList.options
                    }
                  />
                )}
              />
            </div>
          ) : null}

          {resource.type === 'meeting_room' ? (
            <div className="mt-4">
              <Label
                htmlFor={`${entity}-total_slots`}
                className="font-bold text-base text-gray-800"
              >
                Room occupancy limit
              </Label>
              <Input
                type="text"
                variant={isV2 ? "v2Simple" : undefined}
                id={`${entity}-total_slots`}
                {...register('total_slots', {
                  required: true,
                  valueAsNumber: true,
                  validate: (value) =>
                    value.toString() === '' ||
                    Number.parseInt(value.toString(), 10) > 0
                })}
                defaultValue={defaultValues.total_slots || 1}
              />
            </div>
          ) : null}

          {resource.type !== 'area' ? (
            <>
              {/*TODO REMOVE THE FOLLOWING CODE*/}
              {/*<div className="mt-5 flex items-baseline gap-3 shadow-none">
                <Input
                  type="checkbox"
                  id={`is_open_teams`}
                  aria-describedby={`description-is_open`}
                  className="border border-tertiary shadow-none"
                  checked={!isOpen}
                  onChange={(ev) => setValue('is_open', !isOpen)}
                />
                <div>
                  <Label
                    htmlFor={`is_open_teams`}
                    className="relative -top-[0.25rem] cursor-pointer text-base font-bold text-gray-800"
                  >
                    Assigned
                  </Label>
                  <p
                    className="text-base font-normal text-gray-800"
                    id={`description-is_open`}
                  >
                    {entity === 'zone' ? (
                      <>
                        When this box is checked, only assigned teams &amp;
                        users will be able to make reservations in workspaces
                        belonging to {resource?.name || 'this zone'}.
                      </>
                    ) : (
                      <>
                        When this box is checked, only assigned teams &amp;
                        users will be able to make reservations for{' '}
                        {resource?.name || 'this workspace'}.
                      </>
                    )}
                  </p>
                </div>
              </div>*/}
              <div className="mt-5 flex items-baseline gap-3 shadow-none">
                <Input
                  type="checkbox"
                  {...register('is_approvable')}
                  id={`${entity}-is_approvable`}
                  aria-describedby={`description-${entity}-is_approvable`}
                  className="border border-tertiary shadow-none"
                  defaultChecked={defaultValues.is_approvable}
                />

                <div>
                  <Label
                    htmlFor={`${entity}-is_approvable`}
                    className="-top-[0.25rem] relative cursor-pointer font-bold text-base text-gray-800"
                  >
                    Approval Required
                  </Label>
                  <p
                    className="text-base text-gray-800"
                    id={`description-${entity}-is_approvable`}
                  >
                    {entity === 'zone' ? (
                      <>
                        When this box is checked, reservations for workspaces
                        belonging to {resource?.name || 'this zone'} will need
                        to be approved.
                      </>
                    ) : (
                      <>
                        When this box is checked, reservations for{' '}
                        {resource?.name || 'this workspace'} will need to be
                        approved.
                      </>
                    )}
                  </p>
                </div>
              </div>
              <ResourceMakeUnavailableControl
                form={form}
                resource={resource}
                entity={entity}
                name={'is_unavailable'}
              />
            </>
          ) : null}
        </div>
      </div>
  ) : null;
};
